import React, { lazy, Component } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { FadeIn, Eyebrow } from '../components/FadeIn';
import { Carousel } from '../components/Carousel';
import { ContactForm } from '../components/ContactForm';
import { LeafIcon, FlameIcon, BedIcon, UtensilsIcon, MountainIcon, StarsIcon, BookIcon, ArchwayIcon, WaterDropIcon, TreeIcon, SunIcon, MoonIcon, PhoneIcon, EnvelopeIcon, CheckIcon, HomeIcon, WarningIcon, CircleIcon, CircleCheckIcon, CircleXIcon } from '../components/icons';
export default function Home() {
  const {
    scrollY
  } = useScroll();
  const heroY = useTransform(scrollY, [0, 1000], [0, 200]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);
  const whatsappLink = 'https://wa.me/34658077914?text=' + encodeURIComponent('Hola, quiero recibir más información sobre Mas Claror.');
  return <div className="min-h-screen bg-cream overflow-x-hidden" data-id="element-129">
      {/* Header */}
      <header className="sticky top-0 left-0 right-0 z-50 bg-white border-b border-taupe/40" data-id="element-130">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center" data-id="element-131">
          <a href="#" aria-label="Mas Claror — Inicio" className="block" data-id="element-132">
            <img src="https://cdn.magicpatterns.com/uploads/2hMYDGaddGXymzTc6pxzQ6/WhatsApp_Image_2026-05-22_at_5.22.45_p._m..jpg" alt="Mas Claror" className="h-20 sm:h-24 md:h-28 w-auto object-contain" data-id="element-133" />
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-[85vh] sm:min-h-screen w-full overflow-hidden flex items-center justify-center" data-id="element-134">
        <motion.div style={{
        y: heroY
      }} className="absolute inset-0 w-full h-[120%]" data-id="element-135">
          <div className="absolute inset-0 bg-stone/40 z-10" data-id="element-136" />
          <img src="https://cdn.magicpatterns.com/uploads/9VQyW1TwrxukDYachHyRGf/heylloyd-image-1778768929201.png" alt="Mas Claror con un diente de león en primer plano y la masía al fondo" className="w-full h-full object-cover" data-id="element-137" />
        </motion.div>

        <motion.div style={{
        opacity
      }} className="relative z-20 text-center text-cream px-5 sm:px-6 max-w-4xl mx-auto py-16" data-id="element-138">
          <FadeIn delay={0.2} data-id="element-139">
            <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-serif leading-[1.1] mb-6 sm:mb-8" data-id="element-140">
              Un espacio para parar,
              <br data-id="element-141" />
              <span className="italic font-accent" data-id="element-142">respirar</span>
              <br data-id="element-143" />y volver a ti.
            </h1>
          </FadeIn>
          <FadeIn delay={0.6} direction="up" data-id="element-144">
            <p className="text-[11px] sm:text-sm md:text-base tracking-[0.25em] sm:tracking-widest uppercase font-light opacity-90 leading-relaxed" data-id="element-145">
              Masía del siglo XVII · Lluçanès, Cataluña
              <br data-id="element-146" />A hora y media de Barcelona
            </p>
          </FadeIn>
        </motion.div>
      </section>

      {/* Intro — Poetic Block */}
      <section className="py-16 sm:py-20 md:py-32 px-5 sm:px-6 max-w-2xl mx-auto text-center" data-id="element-147">
        <FadeIn data-id="element-148">
          <div className="space-y-7 sm:space-y-8 md:space-y-10 font-serif text-stone leading-relaxed text-base sm:text-xl md:text-2xl" data-id="element-149">
            <p data-id="element-150">
              Hay lugares que no se visitan.
              <br data-id="element-151" />
              <span className="italic" data-id="element-152">Se reconocen.</span>
            </p>
            <p data-id="element-153">Mas Claror no te pide nada.</p>
            <p data-id="element-154">
              Solo abre un espacio donde el ruido baja…
              <br data-id="element-155" />y algo dentro de ti puede volver a aparecer.
            </p>
            <p data-id="element-156">
              El tiempo aquí no se detiene,
              <br data-id="element-157" />
              pero se vuelve más ancho, más habitable.
            </p>
            <p data-id="element-158">El cuerpo se afloja. La mirada se limpia.</p>
            <p data-id="element-159">
              No vienes a hacer más.
              <br data-id="element-160" />
              Vienes a escucharte mejor.
              <br data-id="element-161" />
              <span className="text-black" data-id="element-162">A volver a lo que ya estaba.</span>
            </p>
            <p data-id="element-163">
              Y cuando te vas, sabes que no solo estuviste
              <br data-id="element-164" />
              en un lugar… sino que atravesaste algo.
            </p>
            <div className="w-px h-16 bg-taupe mx-auto" data-id="element-165" />
            <p className="font-accent italic text-xl sm:text-3xl md:text-4xl text-stone" data-id="element-166">
              Un espacio para parar. Para respirar.
              <br data-id="element-167" />
              Para volver a ti.
            </p>
          </div>
        </FadeIn>
      </section>

      {/* The Place */}
      <section className="py-16 sm:py-24 md:py-32 bg-white" data-id="element-168">
        <div className="max-w-7xl mx-auto px-5 sm:px-6" data-id="element-169">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 lg:gap-24 items-center" data-id="element-170">
            <FadeIn direction="right" data-id="element-171">
              <div className="aspect-[3/4] overflow-hidden rounded-sm" data-id="element-172">
                <img src="https://cdn.magicpatterns.com/uploads/jV2EAdDxCjY2eWtWQLS82F/casa.png" alt="Mas Claror al atardecer rodeada de campos" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" data-id="element-173" />
              </div>
            </FadeIn>

            <div data-id="element-174">
              <FadeIn direction="left" data-id="element-175">
                <Eyebrow data-id="element-176">El Lugar</Eyebrow>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif mb-6 md:mb-8 leading-tight" data-id="element-177">
                  Una masía del siglo XVII completamente rehabilitada, decorada
                  con amor, en el corazón del Lluçanès, Alpens.
                </h2>
                <p className="text-xl font-accent italic text-sage mb-12" data-id="element-178">
                  "Cada rincón fue pensado para que el cuerpo descanse y el alma
                  respire."
                </p>

                <div className="space-y-8" data-id="element-179">
                  <h3 className="text-sm uppercase tracking-widest text-stone-light mb-6" data-id="element-180">
                    Lo que encontrarás aquí
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-181">
                    {[{
                    icon: <HomeIcon data-id="element-182" />,
                    text: 'Piedra antigua, volta catalana, luz natural'
                  }, {
                    icon: <TreeIcon data-id="element-183" />,
                    text: 'Campos y bosque en todas las direcciones'
                  }, {
                    icon: <StarsIcon data-id="element-184" />,
                    text: 'Cielo estrellado sin contaminación lumínica'
                  }, {
                    icon: <BookIcon data-id="element-185" />,
                    text: 'Biblioteca con chimenea para el silencio interior'
                  }, {
                    icon: <ArchwayIcon data-id="element-186" />,
                    text: 'Terraza con arcadas y vistas infinitas'
                  }, {
                    icon: <LeafIcon data-id="element-187" />,
                    text: 'Naturaleza viva a tu alrededor, en cada estación'
                  }].map((item, i) => <div key={i} className="flex items-start gap-4" data-id="element-188">
                        <div className="text-clay mt-1" data-id="element-189">{item.icon}</div>
                        <p className="text-sm text-stone-light leading-relaxed" data-id="element-190">
                          {item.text}
                        </p>
                      </div>)}
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* For Organizers — Deep Sage */}
      <section className="py-16 sm:py-24 md:py-32 px-5 sm:px-6 bg-forest text-cream" data-id="element-191">
        <div className="max-w-3xl mx-auto text-center" data-id="element-192">
          <FadeIn data-id="element-193">
            <Eyebrow className="justify-center" data-id="element-194">
              <span className="text-taupe-light" data-id="element-195">
                Para quien organiza retiros
              </span>
            </Eyebrow>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif mb-10 md:mb-12 leading-tight" data-id="element-196">
              Sabes lo que pasa cuando el espacio no está a la altura.
            </h2>
            <div className="space-y-6 text-lg font-light text-taupe-light leading-relaxed" data-id="element-197">
              <p data-id="element-198">
                El grupo llega con ilusión y el lugar los decepciona. La sala es
                pequeña. Las habitaciones incómodas. La comida, un catering
                genérico. Y tú — que pusiste meses de trabajo en ese retiro —
                pasas el fin de semana compensando lo que el espacio no da.
              </p>
              <p className="text-xl font-serif text-cream italic py-4" data-id="element-199">
                Eso no volverá a pasar.
              </p>
              <p data-id="element-200">
                Mas Claror fue pensada, rehabilitada y cuidada para que el
                espacio haga la mitad del trabajo contigo.
              </p>
              <p data-id="element-201">
                Cuando tu grupo cruce esa puerta de piedra y exhale
                profundamente, sabrás que tomaste la decisión correcta.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Spaces */}
      <section className="py-16 sm:py-24 md:py-32 px-5 sm:px-6" data-id="element-202">
        <div className="max-w-7xl mx-auto" data-id="element-203">
          <FadeIn data-id="element-204">
            <div className="text-center mb-12 sm:mb-20" data-id="element-205">
              <Eyebrow className="justify-center" data-id="element-206">Los Espacios</Eyebrow>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif leading-tight" data-id="element-207">
                La Sala — El corazón del retiro
              </h2>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 sm:gap-12 items-center mb-20 sm:mb-32" data-id="element-208">
            <div className="lg:col-span-5 lg:col-start-2" data-id="element-209">
              <FadeIn direction="right" data-id="element-210">
                <p className="text-2xl font-serif leading-relaxed mb-8" data-id="element-211">
                  Una sala que no necesita decoración extra. Ya tiene 300 años
                  de historia.
                </p>
                <ul className="space-y-4 text-stone-light" data-id="element-212">
                  <li className="flex items-center gap-3" data-id="element-213">
                    <CheckIcon size={18} className="text-sage" data-id="element-214" /> 100 metros
                    cuadrados
                  </li>
                  <li className="flex items-center gap-3" data-id="element-215">
                    <CheckIcon size={18} className="text-sage" data-id="element-216" /> Volta catalana
                    original
                  </li>
                  <li className="flex items-center gap-3" data-id="element-217">
                    <CheckIcon size={18} className="text-sage" data-id="element-218" /> Parquet ·
                    Calefacción · Sonido
                  </li>
                  <li className="flex items-center gap-3" data-id="element-219">
                    <CheckIcon size={18} className="text-sage" data-id="element-220" /> Esterillas ·
                    Cojines · Mantas
                  </li>
                  <li className="flex items-center gap-3" data-id="element-221">
                    <CheckIcon size={18} className="text-sage" data-id="element-222" /> Camillas y
                    colchones para masaje
                  </li>
                </ul>
              </FadeIn>
            </div>
            <div className="lg:col-span-6" data-id="element-223">
              <FadeIn direction="left" data-id="element-224">
                <div className="aspect-[4/3] overflow-hidden rounded-sm" data-id="element-225">
                  <img src="https://cdn.magicpatterns.com/uploads/bLrqapjRCEWEWcW1v78vd9/yoga.png" alt="Sala de yoga con arcadas catalanas" loading="lazy" className="w-full h-full object-cover" data-id="element-226" />
                </div>
              </FadeIn>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 lg:gap-24" data-id="element-227">
            {/* Espacios Interiores */}
            <FadeIn delay={0.2} data-id="element-228">
              <h3 className="text-xl sm:text-2xl font-serif mb-6 sm:mb-8 border-b border-taupe pb-4" data-id="element-229">
                Espacios Interiores
              </h3>
              <div className="space-y-6 sm:space-y-8 mb-8 sm:mb-10" data-id="element-230">
                {[{
                icon: <UtensilsIcon data-id="element-231" />,
                title: 'Comedor',
                desc: 'Comidas en grupo, calor compartido'
              }, {
                icon: <BookIcon data-id="element-232" />,
                title: 'Biblioteca con chimenea',
                desc: 'Silencio, lectura, conversaciones profundas'
              }, {
                icon: <BedIcon data-id="element-233" />,
                title: 'Salita',
                desc: 'Rincón íntimo de descanso'
              }, {
                icon: <ArchwayIcon data-id="element-234" />,
                title: 'Terraza con arcadas',
                desc: 'Vistas espectaculares. Para comer, respirar, contemplar'
              }, {
                icon: <HomeIcon data-id="element-235" />,
                title: 'Habitaciones con encanto',
                desc: 'Vigas de madera, piedra vista, luz cálida'
              }].map((item, i) => <div key={i} className="flex gap-4 sm:gap-6" data-id="element-236">
                    <div className="text-clay shrink-0" data-id="element-237">{item.icon}</div>
                    <div data-id="element-238">
                      <h4 className="font-medium text-stone mb-1" data-id="element-239">
                        {item.title}
                      </h4>
                      <p className="text-sm text-stone-light" data-id="element-240">{item.desc}</p>
                    </div>
                  </div>)}
              </div>
              <div className="aspect-[4/5] overflow-hidden rounded-sm" data-id="element-241">
                <img src="https://cdn.magicpatterns.com/uploads/1GVvg8SspyxwdXkKTMiqfh/chimenea_casa.png" alt="Biblioteca con chimenea" loading="lazy" className="w-full h-full object-cover" data-id="element-242" />
              </div>
            </FadeIn>

            {/* Espacios Exteriores */}
            <FadeIn delay={0.4} data-id="element-243">
              <h3 className="text-xl sm:text-2xl font-serif mb-6 sm:mb-8 border-b border-taupe pb-4" data-id="element-244">
                Espacios Exteriores
              </h3>
              <div className="space-y-6 sm:space-y-8 mb-8 sm:mb-10" data-id="element-245">
                {[{
                icon: <LeafIcon data-id="element-246" />,
                title: 'Era antigua',
                desc: 'Actividades al aire libre, salida directa desde la sala'
              }, {
                icon: <WaterDropIcon data-id="element-247" />,
                title: 'Balsa natural',
                desc: 'Contemplación y conexión con el agua'
              }, {
                icon: <TreeIcon data-id="element-248" />,
                title: 'Tarima de madera',
                desc: 'Espacio de desconexión'
              }, {
                icon: <FlameIcon data-id="element-249" />,
                title: 'Zona de barbacoa',
                desc: 'Porche y jardín para momentos de comunidad'
              }, {
                icon: <MountainIcon data-id="element-250" />,
                title: 'Bosque, campos y río',
                desc: 'Caminatas, meditación activa, cielo infinito'
              }].map((item, i) => <div key={i} className="flex gap-4 sm:gap-6" data-id="element-251">
                    <div className="text-sage shrink-0" data-id="element-252">{item.icon}</div>
                    <div data-id="element-253">
                      <h4 className="font-medium text-stone mb-1" data-id="element-254">
                        {item.title}
                      </h4>
                      <p className="text-sm text-stone-light" data-id="element-255">{item.desc}</p>
                    </div>
                  </div>)}
              </div>
              <div className="aspect-[4/5] overflow-hidden rounded-sm" data-id="element-256">
                <img src="https://cdn.magicpatterns.com/uploads/khYLhtK6HaG9DXsurKunon/terraza.png" alt="Terraza con arcadas y mesa puesta con vistas al campo" loading="lazy" className="w-full h-full object-cover" data-id="element-257" />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Rooms */}
      <section className="py-16 sm:py-24 md:py-32 bg-white px-5 sm:px-6" data-id="element-258">
        <div className="max-w-6xl mx-auto" data-id="element-259">
          <div className="text-center mb-12 sm:mb-16" data-id="element-260">
            <Eyebrow className="justify-center" data-id="element-261">Habitaciones</Eyebrow>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif mb-6 leading-tight" data-id="element-262">
              Descansar profundamente es parte de la experiencia.
            </h2>
            <p className="text-stone-light tracking-widest uppercase text-xs sm:text-sm" data-id="element-263">
              Capacidad total: 26 personas · Masía exclusiva
            </p>
          </div>

          {/* Photo gallery — 5 bedroom photos */}
          <FadeIn data-id="element-264">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-12 sm:mb-16" data-id="element-265">
              <div className="aspect-[3/4] overflow-hidden rounded-sm" data-id="element-266">
                <img src="https://cdn.magicpatterns.com/uploads/ovkgecSUEWyCVGnYUhQti9/habitacion_1.png" alt="Habitación con vigas de madera" loading="lazy" className="w-full h-full object-cover" data-id="element-267" />
              </div>
              <div className="aspect-[3/4] overflow-hidden rounded-sm md:mt-8" data-id="element-268">
                <img src="https://cdn.magicpatterns.com/uploads/g95iZXFyuF2faVmbkm5WdT/cuarto_2.png" alt="Habitación con techo abuhardillado" loading="lazy" className="w-full h-full object-cover" data-id="element-269" />
              </div>
              <div className="aspect-[3/4] overflow-hidden rounded-sm" data-id="element-270">
                <img src="https://cdn.magicpatterns.com/uploads/15VhygDDUgYeubFEMaJVBq/WhatsApp_Image_2026-05-08_at_12.24.33_p._m..jpg" alt="Habitación azul con vigas de madera" loading="lazy" className="w-full h-full object-cover" data-id="element-271" />
              </div>
              <div className="aspect-[3/4] overflow-hidden rounded-sm md:mt-8" data-id="element-272">
                <img src="https://cdn.magicpatterns.com/uploads/fJXdfpge1QAjQhEnV7H7JD/WhatsApp_Image_2026-05-08_at_12.24.32_p._m..jpg" alt="Habitación violeta con cama king y cortinas" loading="lazy" className="w-full h-full object-cover" data-id="element-273" />
              </div>
              <div className="aspect-[3/4] overflow-hidden rounded-sm col-span-2 md:col-span-4 md:aspect-[16/7]" data-id="element-274">
                <img src="https://cdn.magicpatterns.com/uploads/tN1EXEHT3Bo1VfKJ1KrUob/WhatsApp_Image_2026-05-08_at_12.41.34_p._m..jpg" alt="Habitación con dos camas y arco interior modernista" loading="lazy" className="w-full h-full object-cover" data-id="element-275" />
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-276">
            <div className="bg-cream p-6 sm:p-8 md:p-12 rounded-sm max-w-3xl mx-auto mb-10 sm:mb-12" data-id="element-277">
              <div className="space-y-5 sm:space-y-6" data-id="element-278">
                {[{
                qty: '4 habitaciones',
                cap: '3 personas c/u',
                bath: 'Baño propio'
              }, {
                qty: '1 habitación',
                cap: '2 personas',
                bath: 'Baño propio'
              }, {
                qty: '1 habitación',
                cap: '1 persona',
                bath: 'Baño propio'
              }].map((room, i) => <div key={i} className="flex justify-between items-center gap-4 border-b border-taupe pb-4 last:border-0" data-id="element-279">
                    <div className="min-w-0" data-id="element-280">
                      <p className="font-medium text-stone" data-id="element-281">{room.qty}</p>
                      <p className="text-sm text-stone-light" data-id="element-282">{room.cap}</p>
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-sage shrink-0 whitespace-nowrap" data-id="element-283">
                      <CheckIcon size={16} data-id="element-284" /> {room.bath}
                    </div>
                  </div>)}
                <div className="pt-4 border-t border-taupe" data-id="element-285">
                  <p className="font-medium text-stone mb-1" data-id="element-286">
                    1 habitación comunitaria
                  </p>
                  <p className="text-sm text-stone-light mb-2" data-id="element-287">11 personas</p>
                  <p className="text-xs text-stone-light italic" data-id="element-288">
                    Compartido · 2 duchas · 2 WC · 2 lavabos.
                    <br data-id="element-289" />
                    Con separaciones de madera para mayor intimidad.
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-290">
            <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-4 sm:gap-y-5 text-sm text-stone-light" data-id="element-291">
              {['Camas individuales amplias (sin literas)', 'Ropa de cama · Nórdico · Manta', 'Toalla de baño y ducha incluidas', 'Opción de unir camas en matrimonio'].map((item, i) => <span key={i} className="flex items-start gap-3" data-id="element-292">
                  <CheckIcon size={16} className="text-clay mt-1 shrink-0" data-id="element-293" />
                  <span className="leading-relaxed" data-id="element-294">{item}</span>
                </span>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Food */}
      <section className="py-16 sm:py-24 md:py-32 px-5 sm:px-6" data-id="element-295">
        <div className="max-w-5xl mx-auto" data-id="element-296">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 items-center" data-id="element-297">
            <FadeIn data-id="element-298">
              <div className="grid grid-cols-2 gap-3 sm:gap-4" data-id="element-299">
                <div className="aspect-[3/4] overflow-hidden rounded-sm" data-id="element-300">
                  <img src="https://cdn.magicpatterns.com/uploads/gtx66FvuphzVmNn8hr4pe2/WhatsApp_Image_2026-05-14_at_4.28.12_p._m..jpg" alt="Bizcochos caseros sobre mesa floreada" loading="lazy" className="w-full h-full object-cover" data-id="element-301" />
                </div>
                <div className="aspect-[3/4] overflow-hidden rounded-sm sm:mt-8" data-id="element-302">
                  <img src="https://cdn.magicpatterns.com/uploads/d5QoemLwE4xs7YW48exLwT/WhatsApp_Image_2026-05-13_at_3.44.01_p._m..jpg" alt="Arroz de verduras con flores silvestres" loading="lazy" className="w-full h-full object-cover" data-id="element-303" />
                </div>
                <div className="aspect-[3/4] overflow-hidden rounded-sm" data-id="element-304">
                  <img src="https://cdn.magicpatterns.com/uploads/qu9JLv9wNVxTBaHv5Syrvi/WhatsApp_Image_2026-05-13_at_3.44.02_p._m._(1).jpg" alt="Ensalada de queso de cabra, granada y setas" loading="lazy" className="w-full h-full object-cover" data-id="element-305" />
                </div>
                <div className="aspect-[3/4] overflow-hidden rounded-sm sm:mt-8" data-id="element-306">
                  <img src="https://cdn.magicpatterns.com/uploads/xjETqYDdJMmSdv8z8iwBSW/WhatsApp_Image_2026-05-13_at_3.44.02_p._m..jpg" alt="Calabacines rellenos gratinados" loading="lazy" className="w-full h-full object-cover" data-id="element-307" />
                </div>
              </div>
            </FadeIn>

            <div data-id="element-308">
              <FadeIn delay={0.2} data-id="element-309">
                <Eyebrow data-id="element-310">Alimentación</Eyebrow>
                <h2 className="text-3xl sm:text-4xl font-serif mb-6 leading-tight" data-id="element-311">
                  Cuidar el cuerpo es parte del retiro.
                </h2>
                <p className="text-base sm:text-lg text-stone-light mb-10 sm:mb-12" data-id="element-312">
                  Pensión completa elaborada por un cocinero con experiencia.
                </p>

                <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-10 sm:mb-12 text-center" data-id="element-313">
                  <div className="p-3 sm:p-4 bg-white rounded-sm" data-id="element-314">
                    <SunIcon className="mx-auto mb-2 sm:mb-3 text-clay" data-id="element-315" />
                    <p className="font-medium text-xs sm:text-sm" data-id="element-316">Desayuno</p>
                    <p className="text-[10px] sm:text-xs text-stone-light mt-1" data-id="element-317">
                      Energético
                    </p>
                  </div>
                  <div className="p-3 sm:p-4 bg-white rounded-sm" data-id="element-318">
                    <UtensilsIcon className="mx-auto mb-2 sm:mb-3 text-sage" data-id="element-319" />
                    <p className="font-medium text-xs sm:text-sm" data-id="element-320">Comida</p>
                    <p className="text-[10px] sm:text-xs text-stone-light mt-1" data-id="element-321">
                      Cocina real
                    </p>
                  </div>
                  <div className="p-3 sm:p-4 bg-white rounded-sm" data-id="element-322">
                    <MoonIcon className="mx-auto mb-2 sm:mb-3 text-stone-light" data-id="element-323" />
                    <p className="font-medium text-xs sm:text-sm" data-id="element-324">Cena</p>
                    <p className="text-[10px] sm:text-xs text-stone-light mt-1" data-id="element-325">
                      Momento mágico
                    </p>
                  </div>
                </div>

                <div className="mb-10 sm:mb-12" data-id="element-326">
                  <h4 className="text-xs sm:text-sm uppercase tracking-widest text-stone-light mb-4" data-id="element-327">
                    Disponible todo el día
                  </h4>
                  <p className="text-sm leading-relaxed text-stone" data-id="element-328">
                    Agua · Fruta · Infusiones · Café · Leches de vaca y
                    vegetales · Bizcocho casero como tentempié
                  </p>
                </div>

                <div className="space-y-4 text-sm" data-id="element-329">
                  <div className="flex justify-between items-start sm:items-center pb-3 border-b border-taupe gap-3 sm:gap-4" data-id="element-330">
                    <span className="flex items-center gap-2 min-w-0" data-id="element-331">
                      <LeafIcon size={16} className="text-sage shrink-0" data-id="element-332" />{' '}
                      <span className="leading-tight" data-id="element-333">
                        Ovo-lácteo-vegetariana
                      </span>
                    </span>
                    <span className="text-stone-light text-right shrink-0 whitespace-nowrap" data-id="element-334">
                      Incluida
                    </span>
                  </div>
                  <div className="flex justify-between items-start sm:items-center pb-3 border-b border-taupe gap-3 sm:gap-4" data-id="element-335">
                    <span className="flex items-center gap-2 min-w-0" data-id="element-336">
                      <UtensilsIcon size={16} className="text-clay shrink-0" data-id="element-337" />{' '}
                      <span className="leading-tight" data-id="element-338">
                        Mediterránea (carne y pescado)
                      </span>
                    </span>
                    <span className="text-stone-light text-right shrink-0 whitespace-nowrap" data-id="element-339">
                      +15€/p/día
                    </span>
                  </div>
                  <div className="flex justify-between items-start sm:items-center pb-3 border-b border-taupe gap-3 sm:gap-4" data-id="element-340">
                    <span className="flex items-center gap-2 min-w-0" data-id="element-341">
                      <UtensilsIcon size={16} className="text-sage shrink-0" data-id="element-342" />{' '}
                      <span className="leading-tight" data-id="element-343">
                        Combinada (Med. + ovo-lácteo veg.)
                      </span>
                    </span>
                    <span className="text-stone-light text-right shrink-0 whitespace-nowrap" data-id="element-344">
                      +7€/p/día
                    </span>
                  </div>
                  <div className="flex justify-between items-start sm:items-center pb-3 border-b border-taupe gap-3 sm:gap-4" data-id="element-345">
                    <span className="flex items-center gap-2 min-w-0" data-id="element-346">
                      <WarningIcon size={16} className="text-stone-light shrink-0" data-id="element-347" />{' '}
                      <span className="leading-tight" data-id="element-348">Intolerancias</span>
                    </span>
                    <span className="text-stone-light text-right shrink-0" data-id="element-349">
                      Siempre se tienen en cuenta
                    </span>
                  </div>
                </div>

                <p className="mt-8 text-center font-accent italic text-lg sm:text-xl text-sage" data-id="element-350">
                  "Nos adaptamos al tipo de alimentación que necesitáis para
                  vuestro retiro."
                </p>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing & Booking — Deep Sage */}
      <section className="py-16 sm:py-24 md:py-32 bg-forest text-cream px-5 sm:px-6" data-id="element-351">
        <div className="max-w-4xl mx-auto" data-id="element-352">
          <div className="text-center mb-12 sm:mb-20" data-id="element-353">
            <FadeIn data-id="element-354">
              <Eyebrow className="justify-center" data-id="element-355">
                <span className="text-taupe-light" data-id="element-356">Precios y Reserva</span>
              </Eyebrow>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif mb-6 leading-tight" data-id="element-357">
                Masía exclusiva para tu grupo.
              </h2>
              <p className="text-taupe-light font-light" data-id="element-358">
                Sin extraños. Sin interrupciones.
              </p>
            </FadeIn>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 sm:gap-16" data-id="element-359">
            <FadeIn delay={0.2} data-id="element-360">
              <h3 className="text-xl sm:text-2xl font-serif mb-6 sm:mb-8 text-clay-light" data-id="element-361">
                Tarifas
              </h3>
              <div className="space-y-5 sm:space-y-6 mb-8" data-id="element-362">
                <div className="flex justify-between items-end gap-3 border-b border-forest-light pb-4" data-id="element-363">
                  <div className="min-w-0" data-id="element-364">
                    <p className="text-base sm:text-lg" data-id="element-365">15 – 26 personas</p>
                    <p className="text-xs text-taupe-light mt-1" data-id="element-366">
                      Incluye 1 pensión gratuita
                    </p>
                  </div>
                  <p className="text-lg sm:text-xl font-serif shrink-0 whitespace-nowrap" data-id="element-367">
                    75 €{' '}
                    <span className="text-xs sm:text-sm font-sans text-taupe-light" data-id="element-368">
                      /pers/día
                    </span>
                  </p>
                </div>
                <div className="flex justify-between items-end gap-3 border-b border-forest-light pb-4" data-id="element-369">
                  <p className="text-base sm:text-lg" data-id="element-370">12 – 14 personas</p>
                  <p className="text-lg sm:text-xl font-serif shrink-0 whitespace-nowrap" data-id="element-371">
                    80 €{' '}
                    <span className="text-xs sm:text-sm font-sans text-taupe-light" data-id="element-372">
                      /pers/día
                    </span>
                  </p>
                </div>
                <div className="flex justify-between items-end gap-3 border-b border-forest-light pb-4" data-id="element-373">
                  <p className="text-base sm:text-lg" data-id="element-374">10 – 11 personas</p>
                  <p className="text-lg sm:text-xl font-serif shrink-0 whitespace-nowrap" data-id="element-375">
                    85 €{' '}
                    <span className="text-xs sm:text-sm font-sans text-taupe-light" data-id="element-376">
                      /pers/día
                    </span>
                  </p>
                </div>
              </div>
              <ul className="text-xs text-taupe-light space-y-2 opacity-90" data-id="element-377">
                <li data-id="element-378">✦ Mínimo: 2 noches</li>
                <li data-id="element-379">
                  ✦ Suplemento alimentación mediterránea: +15€/persona/día
                </li>
                <li data-id="element-380">✦ Suplemento alimentación combinada: +7€/persona/día</li>
                <li data-id="element-381">✦ Grupos menores de 10 personas: consultar</li>
                <li data-id="element-382">✦ Más de 2 retiros al año: consultar</li>
              </ul>
            </FadeIn>

            <FadeIn delay={0.4} data-id="element-383">
              <h3 className="text-xl sm:text-2xl font-serif mb-6 sm:mb-8 text-sage-light" data-id="element-384">
                Proceso de Reserva
              </h3>
              <div className="space-y-5 sm:space-y-6" data-id="element-385">
                <div className="flex gap-4" data-id="element-386">
                  <div className="w-6 h-6 rounded-full border border-sage-light flex items-center justify-center shrink-0 text-xs mt-1" data-id="element-387">
                    1
                  </div>
                  <div data-id="element-388">
                    <p className="text-sm" data-id="element-389">
                      Pago de 400 € + formulario de reserva
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-390">
                  <div className="w-6 h-6 rounded-full border border-sage-light flex items-center justify-center shrink-0 text-xs mt-1" data-id="element-391">
                    2
                  </div>
                  <div data-id="element-392">
                    <p className="text-sm" data-id="element-393">Segundo pago de 400 €</p>
                    <p className="text-xs text-taupe-light mt-1" data-id="element-394">
                      15 días antes del retiro (solo grupos +15 pers)
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-395">
                  <div className="w-6 h-6 rounded-full border border-sage-light flex items-center justify-center shrink-0 text-xs mt-1" data-id="element-396">
                    3
                  </div>
                  <div data-id="element-397">
                    <p className="text-sm" data-id="element-398">Importe restante en efectivo</p>
                    <p className="text-xs text-taupe-light mt-1" data-id="element-399">
                      Al inicio del retiro
                    </p>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>

          {/* Cancellation Policy */}
          <FadeIn delay={0.6} data-id="element-400">
            <div className="mt-16 sm:mt-24 pt-12 sm:pt-16 border-t border-forest-light" data-id="element-401">
              <h3 className="text-center text-xs sm:text-sm uppercase tracking-widest text-taupe-light mb-10 sm:mb-12" data-id="element-402">
                Política de Cancelación
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 sm:gap-12 text-sm" data-id="element-403">
                <div data-id="element-404">
                  <h4 className="font-serif text-lg mb-5 sm:mb-6 text-cream" data-id="element-405">
                    Temporada General
                  </h4>
                  <div className="space-y-4" data-id="element-406">
                    <div className="flex justify-between items-center gap-3" data-id="element-407">
                      <span className="text-taupe-light" data-id="element-408">Más de 30 días</span>
                      <span className="flex items-center gap-2 text-sage-light shrink-0 whitespace-nowrap" data-id="element-409">
                        <CircleCheckIcon size={14} data-id="element-410" /> 100% Devolución
                      </span>
                    </div>
                    <div className="flex justify-between items-center gap-3" data-id="element-411">
                      <span className="text-taupe-light" data-id="element-412">15 a 30 días</span>
                      <span className="flex items-center gap-2 text-clay-light shrink-0 whitespace-nowrap" data-id="element-413">
                        <CircleIcon size={14} data-id="element-414" /> Cambio de fecha
                      </span>
                    </div>
                    <div className="flex justify-between items-center gap-3" data-id="element-415">
                      <span className="text-taupe-light" data-id="element-416">Menos de 15 días</span>
                      <span className="flex items-center gap-2 text-red-400/80 shrink-0 whitespace-nowrap" data-id="element-417">
                        <CircleXIcon size={14} data-id="element-418" /> Sin devolución
                      </span>
                    </div>
                  </div>
                </div>

                <div data-id="element-419">
                  <h4 className="font-serif text-lg mb-5 sm:mb-6 text-cream" data-id="element-420">
                    Temporada Alta
                  </h4>
                  <p className="text-xs text-taupe-light mb-4 -mt-3 sm:-mt-4" data-id="element-421">
                    Fin de Año · Semana Santa · Puentes
                  </p>
                  <div className="space-y-4" data-id="element-422">
                    <div className="flex justify-between items-center gap-3" data-id="element-423">
                      <span className="text-taupe-light" data-id="element-424">Más de 45 días</span>
                      <span className="flex items-center gap-2 text-sage-light shrink-0 whitespace-nowrap" data-id="element-425">
                        <CircleCheckIcon size={14} data-id="element-426" /> 100% Devolución
                      </span>
                    </div>
                    <div className="flex justify-between items-center gap-3" data-id="element-427">
                      <span className="text-taupe-light" data-id="element-428">30 a 45 días</span>
                      <span className="flex items-center gap-2 text-clay-light shrink-0 whitespace-nowrap" data-id="element-429">
                        <CircleIcon size={14} data-id="element-430" /> Cambio de fecha
                      </span>
                    </div>
                    <div className="flex justify-between items-center gap-3" data-id="element-431">
                      <span className="text-taupe-light" data-id="element-432">Menos de 30 días</span>
                      <span className="flex items-center gap-2 text-red-400/80 shrink-0 whitespace-nowrap" data-id="element-433">
                        <CircleXIcon size={14} data-id="element-434" /> Sin devolución
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Image Carousel */}
      <section className="py-14 sm:py-20 md:py-28 px-5 sm:px-6 bg-cream" data-id="element-435">
        <div className="max-w-5xl mx-auto" data-id="element-436">
          <FadeIn data-id="element-437">
            <div className="text-center mb-10 sm:mb-12" data-id="element-438">
              <Eyebrow className="justify-center" data-id="element-439">
                Un paseo por Mas Claror
              </Eyebrow>
            </div>
            <Carousel images={[{
            src: 'https://cdn.magicpatterns.com/uploads/tfeSvpXsDinuLpwTFLyadE/WhatsApp_Image_2026-05-08_at_12.44.30_p._m..jpg',
            alt: 'Estanque natural rodeado de vegetación'
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/qw6rtEzxh52oZ7VSQQeC5N/WhatsApp_Image_2026-05-08_at_12.24.36_p._m..jpg',
            alt: 'Amanecer con niebla sobre los campos'
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/dXU9RvNaz1sFuuRE8tieyz/WhatsApp_Image_2026-05-25_at_5.46.34_p._m..jpg',
            alt: 'Rincón de sillones de mimbre frente al campo y el bosque'
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/wi9F8WCpavcWatEdABQ3kq/WhatsApp_Image_2026-05-22_at_5.19.24_p._m..jpg',
            alt: 'Porche con hamacas y barbacoa rodeado de jardín'
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/jG91fm6qGo5QsH8EttBipA/WhatsApp_Image_2026-05-22_at_5.19.24_p._m._(1).jpg',
            alt: 'Mesa larga bajo las arcadas con vistas al bosque'
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/7zfx5Gwe3vs92dikzwqbPD/WhatsApp_Image_2026-05-22_at_5.19.25_p._m..jpg',
            alt: 'Comedor interior con mesa larga y vigas de madera'
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/1H8gRK8TCf7fdBFekufyrU/WhatsApp_Image_2026-05-22_at_5.19.39_p._m..jpg',
            alt: 'Grupo en actividad al aire libre en el campo'
          }]} data-id="element-440" />
          </FadeIn>
        </div>
      </section>

      {/* Emotional Closing */}
      <section className="py-20 sm:py-24 md:py-48 px-5 sm:px-6 text-center relative overflow-hidden" data-id="element-441">
        <div className="absolute inset-0 opacity-5 pointer-events-none" data-id="element-442">
          <img src="https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?q=80&w=2000&auto=format&fit=crop" alt="Montañas al fondo" className="w-full h-full object-cover" data-id="element-443" />
        </div>

        <div className="max-w-2xl mx-auto relative z-10" data-id="element-444">
          <FadeIn data-id="element-445">
            <p className="text-base sm:text-xl md:text-2xl font-serif text-stone mb-8 sm:mb-10 md:mb-12 leading-relaxed" data-id="element-446">
              Tu grupo merece un espacio que los reciba.
              <br data-id="element-447" />
              No un espacio que tengas que disculpar.
              <br data-id="element-448" />
              No un lugar genérico que olviden al salir del coche.
            </p>

            <p className="text-base sm:text-xl md:text-2xl font-serif text-stone mb-8 sm:mb-10 md:mb-12 leading-relaxed" data-id="element-449">
              Mas Claror lleva 300 años de pie.
              <br data-id="element-450" />
              Ha visto generaciones pasar. Ha visto el cielo llenarse de
              estrellas miles de noches seguidas. Ha escuchado el bosque crecer.
            </p>

            <p className="text-base sm:text-xl md:text-2xl font-serif text-stone mb-12 sm:mb-16 md:mb-20 leading-relaxed" data-id="element-451">
              Y ahora está lista para recibir a tu grupo.
              <br data-id="element-452" />
              Para que lleguen con el ruido del mundo encima.
              <br data-id="element-453" />
              Y se vayan
              <br data-id="element-454" />
              <span className="italic text-sage" data-id="element-455">
                en silencio. En paz. En ellos mismos.
              </span>
            </p>

            <div className="w-px h-10 sm:h-12 md:h-16 bg-taupe mx-auto mb-10 sm:mb-12 md:mb-16" data-id="element-456" />

            <h2 className="text-3xl sm:text-4xl md:text-6xl font-serif leading-tight" data-id="element-457">
              Un espacio para parar.
              <br data-id="element-458" />
              <span className="italic font-accent text-clay" data-id="element-459">Respirar.</span>
              <br data-id="element-460" />Y volver a ti.
            </h2>
          </FadeIn>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="bg-white pt-16 sm:pt-20 md:pt-28 pb-12 px-5 sm:px-6 border-t border-taupe" data-id="element-461">
        <div className="max-w-5xl mx-auto" data-id="element-462">
          <FadeIn data-id="element-463">
            <div className="flex justify-center mb-10 sm:mb-12" data-id="element-464">
              <img src="https://cdn.magicpatterns.com/uploads/2hMYDGaddGXymzTc6pxzQ6/WhatsApp_Image_2026-05-22_at_5.22.45_p._m..jpg" alt="Mas Claror" className="h-24 sm:h-40 md:h-48 object-contain" data-id="element-465" />
            </div>

            <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-16" data-id="element-466">
              <Eyebrow className="justify-center" data-id="element-467">Contacto</Eyebrow>
              <p className="text-xl sm:text-3xl font-serif italic text-stone-light mb-4 leading-relaxed" data-id="element-468">
                Hay lugares que no se visitan.
                <br data-id="element-469" />
                Se recuerdan.
              </p>
              <p className="text-base text-stone mb-10 sm:mb-12" data-id="element-470">
                Mas Claror te está esperando.
              </p>
            </div>

            <ContactForm data-id="element-471" />
          </FadeIn>

          {/* Contact details */}
          <FadeIn delay={0.2} data-id="element-472">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 sm:gap-12 mt-16 sm:mt-20 pt-10 sm:pt-12 border-t border-taupe" data-id="element-473">
              <div data-id="element-474">
                <h3 className="text-xs sm:text-sm uppercase tracking-widest text-stone-light mb-5 sm:mb-6" data-id="element-475">
                  María Navarro
                </h3>
                <div className="space-y-4 text-stone-light text-sm sm:text-base break-words" data-id="element-476">
                  <a href="tel:+34658077914" className="flex items-center gap-3 w-fit hover:text-forest hover:underline transition-[color,text-decoration] duration-200 ease-in-out" data-id="element-477">
                    <PhoneIcon size={18} data-id="element-478" /> +34 658 077 914
                  </a>
                  <a href="mailto:masclaror@hotmail.com" className="flex items-center gap-3 w-fit break-all hover:text-forest hover:underline transition-[color,text-decoration] duration-200 ease-in-out" data-id="element-479">
                    <EnvelopeIcon size={18} data-id="element-480" /> masclaror@hotmail.com
                  </a>
                  <a href="https://instagram.com/masia_masclaror" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 w-fit hover:text-forest hover:underline transition-[color,text-decoration] duration-200 ease-in-out" data-id="element-481">
                    <InstagramIcon size={18} data-id="element-482" /> @masia_masclaror
                  </a>
                </div>

                {/* Quick action buttons */}
                <div className="flex flex-col sm:flex-row gap-3 mt-8" data-id="element-483">
                  <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 text-center bg-sage text-cream px-5 sm:px-6 py-3 text-[11px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-widest hover:bg-forest transition-colors duration-500" data-id="element-484">
                    <WhatsAppIcon size={16} data-id="element-485" /> Contáctanos por WhatsApp
                  </a>
                  <a href="tel:+34658077914" className="inline-flex items-center justify-center gap-2 whitespace-nowrap border border-stone text-stone px-5 sm:px-6 py-3 text-[11px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-widest hover:bg-stone hover:text-cream transition-colors duration-500" data-id="element-486">
                    <PhoneIcon size={16} data-id="element-487" /> +34 658 077 914
                  </a>
                </div>
              </div>

              <div className="md:text-right" data-id="element-488">
                <h3 className="text-xs sm:text-sm uppercase tracking-widest text-stone-light mb-5 sm:mb-6" data-id="element-489">
                  Dónde estamos
                </h3>
                <p className="text-sm text-stone-light leading-relaxed mb-6 sm:mb-8" data-id="element-490">
                  Mas Claror · Alpens
                  <br data-id="element-491" />
                  Lluçanès · Cataluña
                  <br data-id="element-492" />A hora y media de Barcelona · A hora y media de Girona
                </p>
                <p className="text-base sm:text-lg font-accent text-forest leading-relaxed" data-id="element-493">
                  Si quieres conocer el espacio antes de reservar,
                  <br data-id="element-494" />
                  te invitamos a tomarte una infusión aromática
                  <br data-id="element-495" />
                  con plantas de la zona.
                </p>
              </div>
            </div>
          </FadeIn>

          <div className="text-center text-xs text-stone-light/70 pt-12 sm:pt-16 mt-10 sm:mt-12 border-t border-taupe" data-id="element-496">
            © {new Date().getFullYear()} Mas Claror. Todos los derechos
            reservados.
          </div>
        </div>
      </section>
    </div>;
}
// Inline social icons
const InstagramIcon = ({
  size = 18
}: {
  size?: number;
}) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-497">
    <rect width="20" height="20" x="2" y="2" rx="5" ry="5" data-id="element-498" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" data-id="element-499" />
    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" data-id="element-500" />
  </svg>;
const WhatsAppIcon = ({
  size = 18
}: {
  size?: number;
}) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-501">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" data-id="element-502" />
  </svg>;